<template>
<div
  id="app"
  class="app"
  :class="{
    'app--has-bottom-nav-bar': displayBottomNavBar,
    'app--has-broadcast-message': hasBroadcastMessage,
  }"
  :style="{
    '--sidebar-width': isSidebarFixed ? sidebarWidth : '0px',
    '--header-height': headerHeight,
  }"
>
  <broadcast-message v-if="hasBroadcastMessage" />
  <component :is="headerComponent"/>

  <transition name="sidebar-slide">
    <component
      :is="sidebarMenuComponent"
      v-if="sidebarIsOpen || isSidebarFixed"
    />
  </transition>
  <transition name="fade"
              mode="out-in"
              appear
              v-on:before-leave="beforeTransitionLeave"
              @after-leave="$root.$emit('router-view-transition-leave')"
              v-on:after-enter="afterTransitionEnter"
  >
    <slot></slot>
  </transition>
  <page-footer></page-footer>
  <BottomNavBar v-if="displayBottomNavBar"/>
  <div v-if="sidebarIsOpen" class="sidebar-close-mask" @click="setSidebarState(false)"></div>
  <media-query-helper v-if="false"></media-query-helper>
  <div class="cart-modal-wrapper">
    <transition name="slide-sidebar-modal">
      <keep-alive>
        <modal-view
          v-if="cartModalIsActive"
          :protected="cartModalIsProtected"
          @dismiss="toggleCartModal"
          :covers-appbar="true"
        >
          <cart-modal @dismiss="toggleCartModal" @protect="protectCartModal" @unprotect="unprotectCartModal"></cart-modal>
        </modal-view>
      </keep-alive>
    </transition>
  </div>
  <div class="auth-modal-wrapper">
    <transition name="slide-sidebar-modal">
      <keep-alive>
        <modal-view v-if="authModalIsActive" :active="authModalIsActive" @dismiss="toggleAuthModal">
          <AuthModal @dismiss="toggleAuthModal"/>
        </modal-view>
      </keep-alive>
    </transition>
  </div>
  <div class="promotion-reward-selector-wrapper">
    <promotion-reward-selector v-if="rewardSelector.available"
                               :promotion-id="rewardSelector.promotion_id"
                               :reward-id="rewardSelector.reward_id"
                               :context="rewardSelector.context"
                               :max-quantity="rewardSelector.max_quantity"
    />
  </div>
  <transition name="opacity">
    <div
      v-if="shouldDisplayDropdownMask"
      class="modal-mask"
    ></div>
  </transition>
  <extensions-slot></extensions-slot>
</div>
</template>

<style lang="less">
  @import "@/assets/less/zenky";
</style>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations } from 'vuex'
import addConditionalRoutes from '@/router/addConditionalRoutes';
import axios from '@/zenky'
import withModals from '@/mixins/withModals';
import isIOS from '@/helpers/isIOS';
import sidebarWidth from '@/helpers/sidebarWidth';
import isScreenConditionTrue from '@/helpers/isScreenConditionTrue';
// import { height as headerMobileHeight } from '@/components/headers/HeaderMobile';
// import { height as headerNonMobileHeight } from '@/components/headers/HeaderNonMobile';
import BroadcastMessage from '@/components/notifications/BroadcastMessage'

let body = null

const mobileAppbarsBreakpoint = 'sm';

export default {
  mixins: [withModals],

  metaInfo() {
    if (!this.favicon) {
      return {}
    }

    return {
      link: [
        { rel: 'icon', href: this.favicon }
      ]
    }
  },

  components: {
    BroadcastMessage,
    HeaderMobile: () => import('@/components/headers/HeaderMobile'),
    HeaderNonMobile: () => import('@/components/headers/HeaderNonMobile'),
    HeaderNonMobile2: () => import('@/components/headers/HeaderNonMobile2'),

    WithPopup: () => import('@/components/sidebars/for-export/WithPopup'),
    InlineNested: () => import('@/components/sidebars/for-export/InlineNested'),

    BottomNavBar: () => import('@/components/footers/BottomNavBar'),
    PageFooter: () => import(/* webpackPreload: true */ '@/components/footers/PageFooter'),
    MediaQueryHelper: () => import('@/components/dev-tools/MediaQueryHelper'),
    ModalView: () => import('@/components/modals/ModalView'),
    CartModal: () => import('@/components/cart/CartModal'),
    AuthModal: () => import('@/components/auth/AuthModal'),
    ExtensionsSlot: () => import(/* webpackPreload: true */ '@/components/extensions/ExtensionsSlot'),
    PromotionRewardSelector: () => import('@/components/promotions/PromotionRewardSelector'),
  },

  computed: {
    ...mapGetters({
      sidebarIsOpen: 'ui/sidebarIsOpen',
      cartModalIsActive: 'ui/cartModalIsActive',
      authModalIsActive: 'ui/authModalIsActive',
      isBreakpointActive: 'ui/isBreakpointActive',
      shouldDisplayDropdownMask: 'ui/shouldDisplayDropdownMask',
      favicon: 'settings/favicon',
      hasBroadcastMessage: 'store/hasBroadcastMessage',
      rewardSelector: 'order/rewardSelector',
    }),
    displayBottomNavBar() {
      return this.headerType === 'mobile';
    },
    headerType() {
      if (this.isBreakpointActive(mobileAppbarsBreakpoint) || this.$store.state.settings.appbars.mobile_variant === 'mm02') {
        return 'nonMobile'
      }

      return 'mobile';
    },
    headerComponent() {
      if (this.headerType === null) return 'div';
      if (this.headerType === 'mobile') {
        return this.$options.components.HeaderMobile;
      } else {
        switch (this.$store.state.settings.appbars.desktop_variant) {
          case '1': return this.$options.components.HeaderNonMobile;
          case '2': return this.$options.components.HeaderNonMobile2;
        }
      }
    },
    headerHeight() {
      const components = this.$options.components;
      switch (this.headerComponent) {
        // TODO instead import them from component files. When it doesn't cause their chunks to be merged into this one.
        case components.HeaderMobile: return '56px'; // `headerMobileHeight`
        case components.HeaderNonMobile: return '56px'; // `headerNonMobileHeight`
        case components.HeaderNonMobile2: return '90px';
      }
    },
    sidebarMenuComponent() {
      switch (this.$store.state.settings.sidebar.variant) {
        case '1': return this.$options.components.WithPopup;
        case '2': return this.$options.components.InlineNested;
        default: throw new Error('Unexpected sidebar block variant name');
      }
    },
    sidebarWidth() {
      return sidebarWidth;
    },
    isSidebarFixed() {
      return isScreenConditionTrue(this.$store, this.$store.state.settings.sidebar.fixed);
    }
  },

  created() {
    addConditionalRoutes(this.$router, this.$store);

    // Using imperative $watch instead of declarative so we'll see an error if such property doesn't exist.

    const state = this.$store.state;
    this.$watch(() => !!state.auth.user?.customer?.id && !!state.order.order?.id, async userAndOrderPresent => {
      // For the case when an order was created, and then the user logged in.
      // We need this to create bonuses transactions before an order is submitted.
      if (userAndOrderPresent && !state.order.order.customer?.id) {
        await axios.post(`/orders/${state.order.order.id}/customer`, {
          customer: { id: state.auth.user.customer.id },
          token: state.order.order.token,
        });
        await this.$store.dispatch('order/loadOrder', {
          orderId: state.order.order.id,
          orderToken: state.order.order.token,
        });
      }
    });

    this.$watch(() => !!this.$store.state.auth.user?.id, n => {
      if (n) {
        this.$store.dispatch('auth/loadLoyaltyProfile');
      }
    }, { immediate: true });

    this.$watch(
      // The order's city is not the same as the active city.
      () => (
                this.$store.state.order.order?.id
                && this.$store.state.store.activeCityId
                && this.$store.state.store.activeCityId !== this.$store.state.order.order.city?.id
      ),
      async n => {
        if (!n) return;
        const order = this.$store.state.order.order;
        await axios.post(`orders/${order.id}/city`, {
          city_id: this.$store.state.store.activeCityId,
          token: order.token,
        });
        await this.$store.dispatch('order/loadOrder', {
          orderId: order.id,
          orderToken: order.token,
        });
      },
      { immediate: true },
    );

    this.$watch(() => this.$store.state.order.order?.id, () => this.$store.dispatch('order/setBonusesAmount', 0));

    this.$watch(
      () => this.$store.getters['ui/bodyScrollBlocked'],
      async n => {
        (await import('@/helpers/toggleBodyScroll'))[n ? 'blockBodyScroll' : 'unblockBodyScroll']();
      },
      { immediate: true },
    );
  },

  beforeMount() {
    this.updateScreenWidth();
    window.addEventListener('resize', this.updateScreenWidth);
  },

  mounted() {
    Vue.prototype.window = window; // So `window` can be accessed from within templates.

    body = document.getElementsByTagName('body')[0];

    // Disable input zooming on focus on iOS.
    // https://stackoverflow.com/a/57527009/10406353
    // Currently it doesn't actually prevent iOS users from manually zooming, but if one day it does, this may need
    // to be rewritten.
    if (process.env.NODE_ENV !== 'production') {
      const el = document.querySelector('meta[name=viewport]');
      if (el.getAttribute('content').includes('maximum-scale')) {
        console.error('`maximum-scale` attribute is already present on the `viewport` element. '
                + 'You need to update the next code block');
      }
    }
    if (isIOS()) {
      const el = document.querySelector('meta[name=viewport]');
      el.setAttribute('content', `${ el.getAttribute('content') },maximum-scale=1.0`);
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateScreenWidth);
  },

  methods: {
    ...mapMutations({
      setSidebarState: 'ui/setSidebarState',
      toggleCartModalAction: 'ui/toggleCartModal',
      toggleAuthModal: 'ui/toggleAuthModal',
    }),

    updateScreenWidth() {
      this.$store.dispatch('ui/setScreenWidth', window.innerWidth);
    },

    protectCartModal() {
      this.cartModalIsProtected = true
    },

    unprotectCartModal() {
      this.cartModalIsProtected = false
    },

    toggleCartModal() {
      if (this.cartModalIsActive) {
        this.$root.$emit('cartModalIsClosing')
      } else {
        this.$root.$emit('cartModalIsOpening')
      }

      this.toggleCartModalAction()
    },

    beforeTransitionLeave() {
      if (!body) {
        body = document.getElementsByTagName('body')[0]
      }

      body.classList.add('without-horizontal-scroll')
    },

    afterTransitionEnter() {
      if (!body) {
        body = document.getElementsByTagName('body')[0]
      }

      body.classList.remove('without-horizontal-scroll')
    },
  },

  data() {
    return {
      cartModalIsProtected: false,
      modalDescriptors: [
        {
          isActive: 'cartModalIsActive',
          id: {},
        },
        {
          isActive: 'authModalIsActive',
          id: {},
        }
      ],
    }
  }
}
</script>

<style lang="less">
@import '~@/assets/less/components/modal-mask';

.app {
    @apply flex flex-col min-h-screen leading-tight;
    --bottom-nav-bar-height: 0px;
    &--has-bottom-nav-bar {
        --bottom-nav-bar-height: theme('customVars.bottomNavBar.height');
    }
}
.sidebar-close-mask {
  @apply fixed inset-y-0 inset-x-0 z-sidebar-mask;
  background: rgba(0, 0, 0, .3);
}

.sidebar-slide-enter,
.sidebar-slide-leave-to {
  transform: translateX(-100%);
}
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: transform .3s ease;
}

.page {
  @apply flex-grow;
  // So if there's little content on the page during loading, the footer isn't shifted as much when the we're done
  // loading. `@peek-height` is there to remind the user that there's also something at the bottom of the page if it's
  // half empty.
  @peek-height: theme('height.8');
  min-height: calc(100vh - var(--header-height) - var(--bottom-nav-bar-height) - @peek-height);
  margin-left: var(--sidebar-width);
}
.page-header-fixed ~ .page {
  margin-top: var(--header-height);
}

// So it doesn't cover the bottom nav bar.
.grecaptcha-badge {
    bottom: 75px !important;
}
</style>
